/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: inline-block;
}

slot {
  display: none;
}

.qds-container {
  box-sizing: border-box;
  display: grid;
  grid-template-areas: 'select';
  width: inherit;
}

.qds-icon,
.qds-select {
  color: var(--qds-theme-control-text-standard);
  grid-area: select;
}

.qds-icon {
  margin-inline-end: calc(
    var(--qds-control-input-padding-horizontal) +
      var(--qds-control-border-width)
  );
  place-self: center end;
  pointer-events: none;
}

.qds-select {
  appearance: none;
  background-color: var(--qds-theme-control-input-background);
  border-color: var(--qds-theme-control-border);
  border-radius: var(
      --qds-control-border-radius-top-left,
      var(--qds-control-border-radius)
    )
    var(--qds-control-border-radius-top-right, var(--qds-control-border-radius))
    var(
      --qds-control-border-radius-bottom-right,
      var(--qds-control-border-radius)
    )
    var(
      --qds-control-border-radius-bottom-left,
      var(--qds-control-border-radius)
    );
  border-style: solid;
  border-width: var(--qds-control-border-width)
    var(--qds-control-border-width-inline-end, var(--qds-control-border-width))
    var(--qds-control-border-width-block-end, var(--qds-control-border-width))
    var(--qds-control-border-width);
  margin: 0;
  outline: 0;
  padding-block: 0;
  padding-inline: var(--qds-control-input-padding-horizontal);

  &:not(:focus)[aria-invalid='true'] {
    border-color: var(--qds-theme-feedback-result-failure);
  }
}

.qds-select:disabled,
.qds-select:disabled + .qds-icon {
  opacity: var(--qds-theme-disabled);
}

:is(
    ::slotted(optgroup),
    ::slotted(optgroup:not(:disabled) > option),
    ::slotted(option)
  ):disabled {
  opacity: var(--qds-theme-disabled);
}

::slotted(optgroup) {
  font: var(--qds-accessory-section-title);
}

::slotted(optgroup > option),
::slotted(option) {
  font: var(--qds-list-item-label-main);
}

[data-size='small'] {
  &.qds-select {
    font: var(--qds-control-small-text);

    &:not([multiple]) {
      height: var(--qds-control-small-height);
      padding-inline-end: calc(
        var(--qds-control-small-icon-size) + var(--qds-text-icon-gap) +
          var(--qds-control-input-padding-horizontal)
      );
    }
  }

  &.qds-icon {
    height: var(--qds-control-small-icon-size);
    width: var(--qds-control-small-icon-size);
  }
}

[data-size='standard'] {
  &.qds-select {
    font: var(--qds-control-standard-text);

    &:not([multiple]) {
      height: var(--qds-control-standard-height);
      padding-inline-end: calc(
        var(--qds-control-standard-icon-size) + var(--qds-text-icon-gap) +
          var(--qds-control-input-padding-horizontal)
      );
    }
  }

  &.qds-icon {
    height: var(--qds-control-standard-icon-size);
    width: var(--qds-control-standard-icon-size);
  }
}

[data-size='large'] {
  &.qds-select {
    font: var(--qds-control-large-text);

    &:not([multiple]) {
      height: var(--qds-control-large-height);
      padding-inline-end: calc(
        var(--qds-control-large-icon-size) + var(--qds-text-icon-gap) +
          var(--qds-control-input-padding-horizontal)
      );
    }
  }

  &.qds-icon {
    height: var(--qds-control-large-icon-size);
    width: var(--qds-control-large-icon-size);
  }
}

.qds-focus-ring {
  border-radius: max(
      1px,
      var(
        --qds-control-border-radius-top-left,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-top-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-left,
        var(--qds-control-border-radius)
      )
    );
  grid-area: select;
  isolation: isolate;
  outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
  outline-offset: var(--qds-focus-border-offset);
  pointer-events: none;
  visibility: hidden;
  width: calc(
    100% + var(--qds-control-border-width) -
      var(
        --qds-control-border-width-inline-end,
        var(--qds-control-border-width)
      )
  );
  height: calc(
    100% + var(--qds-control-border-width) -
      var(--qds-control-border-width-block-end, var(--qds-control-border-width))
  );
}

.qds-select:focus-visible ~ .qds-focus-ring {
  visibility: visible;
}
